import { useEffect, useState, type FC, type ReactNode, type UIEventHandler } from 'react';
import { type PopoverOrigin } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

interface SelectElementProps {
  options: Array<Record<string, string>>;
  label: string | ReactNode;
  optionLabel: string;
  defaultValue?: string | ReactNode;
  selectedId?: string;
  onChange: (target: unknown) => void;
  idAttribute?: string;
  displayLabelAs?: (label: string) => string;
  className?: string;
  onScroll?: UIEventHandler;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
}

const SelectElement: FC<SelectElementProps> = ({
  options,
  label,
  optionLabel,
  defaultValue,
  selectedId,
  onChange,
  idAttribute,
  displayLabelAs,
  className,
  anchorOrigin,
  transformOrigin,
  onScroll,
}) => {
  const [validOptions, setValidOptions] = useState(options);
  const [selectedOptionId, setSelectedOptionId] = useState(selectedId || '');

  useEffect(() => {
    setValidOptions(options);
    if (!selectedId && !defaultValue && idAttribute) onChange(options[0][idAttribute]);
  }, [options, onChange, idAttribute, selectedId, defaultValue]);

  useEffect(() => {
    if (selectedId) setSelectedOptionId(selectedId);
  }, [selectedId]);

  return (
    <div className={`select-element-container d-flex flex-row ${className}`}>
      <span className="subtitle mr-2">{label}</span>
      <FormControl className="connector-select-form">
        <Select
          multiple={false}
          label="select-element"
          notched={false}
          variant="outlined"
          id="connector-dropdown-select"
          MenuProps={{
            classes: { list: 'max-height-300' },
            anchorOrigin,
            transformOrigin,
            PaperProps: {
              onScroll,
            },
          }}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          value={selectedOptionId}
          displayEmpty={!!defaultValue}
        >
          {defaultValue ? (
            <MenuItem selected disabled value="">
              {defaultValue}
            </MenuItem>
          ) : null}
          {idAttribute && optionLabel && displayLabelAs
            ? validOptions.map((option) => (
                <MenuItem key={option[idAttribute]} value={option[idAttribute]}>
                  {displayLabelAs(option[optionLabel])}
                </MenuItem>
              ))
            : null}
        </Select>
      </FormControl>
    </div>
  );
};

// eslint-disable-next-line @eslint-react/no-default-props, @typescript-eslint/no-deprecated -- this file were not edited, make no sense to fix it now (EVX-3613)
SelectElement.defaultProps = {
  label: '',
  optionLabel: 'name',
  idAttribute: 'id',
  onChange: undefined,
  displayLabelAs: (label: string) => label,
  className: '',
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

export default SelectElement;
